import { motion, AnimatePresence } from 'framer-motion'
import React, { useState, useEffect } from 'react'
import CartItem from './CartItem'
import { useCartState, useCartDispatch } from '@/contexts/CartContext'
import Link from 'next/link'
import { toast } from 'react-hot-toast'
import CartSteps from './CartSteps'
import useAuth from '@/hooks/useAuth'
import CartConfirmationModal from './CartConfirmationModal'
import CouponForm from './CouponForm'
import { useLoginModal } from '@/contexts/LoginModalContext'
import CustomToast from '../CustomToast'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useCurrency } from '../DesktopHeader'
import { track } from '@/lib/tracker'

type Props = {
    isOpen: boolean
    onClose: () => void
    children?: React.ReactNode
}

const modalVariants = {
    hidden: {
        opacity: 0,
        scale: 0.5,
        x: '-25vw',
        y: '40vh',
    },
    visible: {
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
        transition: {
            type: 'spring',
            stiffness: 300,
            damping: 30,
            delay: 0.1 // Wait for overlay to fade in
        }
    },
    exit: {
        opacity: 0,
        scale: 0.5,
        x: '-40vw',
        y: '40vh',
        transition: { duration: 0.2 }
    }
}

const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.3 } },
    exit: { opacity: 0, transition: { duration: 0.3 } }
}

const CartModal = ({ isOpen, onClose, children}: Props) => {
    const {t} = useTranslation('alerts');
    const {t: tCart} = useTranslation('cart');
    const {locale} = useRouter();
    const [isModalVisible, setIsModalVisible] = useState(isOpen)
    const cart = useCartState()
    const {symbol} = useCurrency(cart.currency);
    const cartDispatch = useCartDispatch()
    const { user } = useAuth('none')
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = React.useState(false)
    const { setIsOpen, setPostLoginCallback, setLocation } = useLoginModal()
    const [isCouponLoading, setIsCouponLoading] = React.useState(false)

    useEffect(() => {
        if (isOpen) {
            setIsModalVisible(true)
            track({event_name: 'view_cart'})
        } else {
            setIsModalVisible(false)
        }
    }, [isOpen])

    const handleCheckout = () => {
        if (!user) {
            toast.custom((tel) => <CustomToast type='error' t={tel} message={t('login_first')} />, {
                duration: 2500,
            });
            // Store post-login action: reopen confirmation modal
            setPostLoginCallback(() => () => {
                setIsConfirmationModalOpen(true)
            })
            setLocation('header')
            setIsOpen(true)
            return
        }
        setIsConfirmationModalOpen(true)
    }

    const cartModalTotal = [
    cart.subtotal,
    `-${cart.discount_total}`,
    ]
    .map((v) => parseInt(v.toString(), 10))
    .reduce((a, b) => a + b, 0);


    return (
        <>
        <AnimatePresence initial={false} onExitComplete={onClose}>
          {isOpen && isModalVisible && (
            <motion.div className="fixed inset-0 z-[105] flex items-center justify-center ">
              <motion.div
                className="absolute inset-0 bg-black bg-opacity-50"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={overlayVariants}
                onClick={() => setIsModalVisible(false)}
              />
              <motion.div
                className="relative z-10 pointer-events-auto bg-white rounded-2xl w-[90vw] max-w-[500px] max-h-[95vh] h-[90vh] flex flex-col"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={modalVariants}
              >
                <div className="flex items-center justify-between px-2 border-b" dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}>
                    <div className="flex items-center gap-2 p-2">
                      <span className="text-primary text-2xl font-bold p-2">{tCart('cartModalText')}</span>
                      <span className="bg-gray-200 text-primary rounded px-2 text-xs leading-none ml-2 flex items-center justify-center h-6">{cart.items.length} {tCart('cartModalItems')}</span>
                  </div>
                  <button onClick={onClose} className="text-4xl px-2 rounded-lg text-gray-700  bg-gray-300">×</button>
                </div>
                <CartSteps />
                <div className="flex-1 overflow-y-auto px-4">
                  {cart.items.map((item, idx) => (
                      <div key={idx} className="flex items-center mb-4">
                        <CartItem item={item} index={idx} isItemInFloatingCart={true}/>
                    </div>
                  ))}
                </div>
                <div className='p-4'>
                <CouponForm
                    isCouponLoading={isCouponLoading}
                    setIsCouponLoading={setIsCouponLoading}
                />
                </div>
                <div 
                    className="p-4 border-t mt-auto"
                    dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}
                >
                    <div className="flex justify-between mb-2">
                        <span>{tCart('cartSubTotal')}</span>
                        <span className="font-bold">{cart.subtotal} {symbol} </span>
                    </div>
                    <div>
                        {cart.coupon && (
                        <li className="flex items-center justify-between mt-2 mb-2">
                            <p className="text-gray-400 text-xs">
                                {tCart('cartDiscountAmount')}
                            </p>
                            <strong className="text-sm text-red flex items-center">
                                 {symbol} {cart.discount_total}-
                            </strong>
                        </li>
                    )}
                    </div>
                    {/* <div className="flex justify-between mb-2">
                        <span>{tCart('cartModalDiscountText')}</span>
                        <span className="font-bold">{0.00} {symbol} </span>
                    </div> */}
                    <div className="flex justify-between mb-4">
                        <span className="font-bold text-primary">{tCart('cartModalTotal')}</span>
                        <span className="font-bold text-emerald-500">{cartModalTotal} {symbol} </span>
                    </div>
                    <button 
                        onClick={handleCheckout}
                        className="w-full bg-primary text-sm text-white py-3 rounded-lg font-bold mb-2"
                    >
                        {tCart('cartModalFinish')}
                    </button>
                    <Link href="/products" onClick={onClose}>
                        <button className="w-full border border-primary text-primary py-2 rounded-lg font-bold text-sm">{tCart('cartModalAddMore')}</button>
                    </Link>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <CartConfirmationModal
          isOpen={isConfirmationModalOpen}
          setIsOpen={setIsConfirmationModalOpen}
          onProceed={() => {
            setIsConfirmationModalOpen(false);
            onClose();
          }}
        />
        </>
    )
}

export default CartModal